/**
 * Facettes « Template » et « Épreuve » des tables de campagnes et de parcours,
 * partagées client / admin.
 *
 * Une campagne porte PLUSIEURS templates, un parcours PLUSIEURS épreuves : le
 * filtre est donc une appartenance à une liste, pas une égalité — cocher
 * « PAMI » garde ce qui contient un PAMI, avec ou sans le reste.
 *
 * Vit ici plutôt que dans chaque `colonnes*.tsx` parce que les deux portails
 * listent les mêmes campagnes et les mêmes parcours : deux copies dériveraient
 * sur les libellés.
 */
import {
  filtreListe,
  type ColonneDataTable,
  type FacetteFiltre,
  type RowData,
} from "@repo/ui";
import { APPLICATIONS, type ApplicationEnum } from "@repo/utils";
import type { Doc } from "../../../convex/_generated/dataModel";

/**
 * Libellés des templates connus. Ceux qui manquent s'affichent bruts : la liste
 * des templates ENT_STRUCT grossit à chaque référentiel, la tenir à jour ici
 * coûterait plus qu'elle ne rapporte.
 */
export const LIBELLES_TEMPLATE: Record<string, string> = {
  pami: "PAMI",
  copsoq3: "COPSOQ 3",
  autodiag: "Autodiagnostic",
  egogramme: "Égogramme",
  "pc-fit": "PC Fit",
  "referentiel-pami": "Référentiel PAMI",
  "referentiel-pc-fit": "Référentiel PC Fit",
  referentiel: "Référentiel",
  default: "Inbasket standard",
  rapide: "Inbasket rapide",
};

/** Épreuves sans template : c'est le type qui les nomme. */
export const LIBELLES_TYPE_EVAL: Record<string, string> = {
  inbasket: "Inbasket",
  form: "Questionnaire",
  "fairview": "FairView",
  resultats: "Résultats",
};

const LIBELLES_EPREUVE: Record<string, string> = {
  ...LIBELLES_TYPE_EVAL,
  ...LIBELLES_TEMPLATE,
};

/** Libellé lisible d'une épreuve, sa clé brute à défaut. */
export function libelleEpreuve(cle: string): string {
  return LIBELLES_EPREUVE[cle] ?? cle;
}

const LIBELLES_APPLICATIONS: Record<string, string> = Object.fromEntries(
  APPLICATIONS.map((a) => [a.cle, a.libelle]),
);

/** Composition d'une campagne, sur une colonne d'id `applications`. */
export const FACETTE_APPLICATIONS: FacetteFiltre = {
  colonne: "applications",
  libelle: "Épreuves",
  libelles: LIBELLES_APPLICATIONS,
  ordre: APPLICATIONS.map((a) => a.cle),
};

/**
 * Colonne « Épreuves » d'une liste de campagnes : sa composition
 * (« PAMI + FairView »), qui remplace l'ancien type de parcours. Filtrable
 * par appartenance, comme les templates.
 */
export function colonneApplications<T extends RowData>(
  lire: (ligne: T) => ApplicationEnum[],
  largeur: string,
): ColonneDataTable<T> {
  const texte = (ligne: T) =>
    lire(ligne)
      .map((cle) => LIBELLES_APPLICATIONS[cle] ?? cle)
      .join(" + ");
  return {
    id: "applications",
    header: "Épreuves",
    enableSorting: false,
    accessorFn: texte,
    ...colonneListeFiltrable<T>(lire),
    cell: ({ row }) => (
      <span className="text-sm">{texte(row.original) || "—"}</span>
    ),
    meta: { libelle: "Épreuves", largeur },
  };
}

/** Facette des templates d'une campagne, sur une colonne d'id `templates`. */
export const FACETTE_TEMPLATE: FacetteFiltre = {
  colonne: "templates",
  libelle: "Template",
  libelles: LIBELLES_TEMPLATE,
};

/** Épreuves du parcours, toutes confondues. */
export const FACETTE_EPREUVES: FacetteFiltre = {
  colonne: "epreuves",
  libelle: "Épreuve",
  libelles: LIBELLES_EPREUVE,
};

/**
 * Partie filtrage/facettes d'une colonne dont la ligne porte une liste, à
 * étaler dans sa `ColumnDef` (l'affichage de la cellule reste à l'appelant).
 *
 * `getUniqueValues` est indispensable : sans lui les facettes compteraient la
 * chaîne jointe (« pami, default ») comme une seule valeur, et « PAMI » ne
 * serait jamais proposé.
 */
export function colonneListeFiltrable<T extends RowData>(
  champ: (ligne: T) => string[],
): Pick<ColonneDataTable<T>, "getUniqueValues" | "filterFn"> {
  return {
    getUniqueValues: (ligne) => champ(ligne),
    filterFn: filtreListe<T>(champ),
  };
}

/** Résumé d'épreuve tel que le projette le backend (`evaluationsResume`). */
export type ResumeEpreuve = {
  typeEval: Doc<"evaluations">["typeEval"];
  template?: string;
  statut: Doc<"evaluations">["statut"];
};

/**
 * Clé d'une épreuve : son template quand il y en a un (deux `form` ne sont pas
 * la même épreuve : un PAMI n'est pas un égogramme), son type sinon.
 */
function cleEpreuve(e: ResumeEpreuve): string {
  return e.template ?? e.typeEval;
}

/** Épreuves d'un parcours, dédoublonnées (une campagne peut en répéter une). */
export function epreuves(resume: ResumeEpreuve[]): string[] {
  return [...new Set(resume.map(cleEpreuve))];
}

/**
 * La colonne filtre-seule « Épreuves ».
 *
 * Masquée par défaut : elle n'existe que pour porter une facette (qui lit une
 * colonne, pas des données libres), et reste activable via « Colonnes » pour
 * qui veut lire le détail. Écrite une fois : les deux portails affichent les
 * mêmes épreuves des mêmes parcours.
 */
export function colonnesEpreuves<T extends RowData>(
  lire: (ligne: T) => ResumeEpreuve[],
): ColonneDataTable<T>[] {
  const toutes = (ligne: T) => epreuves(lire(ligne));
  const texte = (cles: string[]) => cles.map(libelleEpreuve).join(", ");

  return [
    {
      id: "epreuves",
      header: "Épreuves",
      enableSorting: false,
      accessorFn: (ligne) => texte(toutes(ligne)),
      ...colonneListeFiltrable<T>(toutes),
      cell: ({ row }) => (
        <span className="text-sm">{texte(toutes(row.original)) || "—"}</span>
      ),
      meta: { libelle: "Épreuves", largeur: "14%", masqueeParDefaut: true },
    },
  ];
}
